// Admin — Screen Recording policy + recordings library.
// Policy: a tenant default + per-campaign overrides (most-specific wins).
// Recordings: filterable list with inline WebM playback and download/delete.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, Save, MonitorPlay, Film, Trash2, Download, Plus, X, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const MODES = [
  { key: "call", label: "During live calls", hint: "Records only while a call is connected" },
  { key: "ready", label: "While Ready", hint: "Records the whole time the agent is Available" },
  { key: "omni", label: "Omnichannel handling", hint: "Records while handling chat / email / WhatsApp" },
];
const QUALITIES = ["low", "medium", "high"];
const RETENTIONS = [7, 30, 60, 90, 180, 365];

function PolicyEditor({ value, onChange, showRetention, disabled, idSuffix = "" }) {
  const p = value || {};
  const tid = (base) => `${base}${idSuffix}`;
  const setModes = (key, on) => {
    const cur = new Set(p.modes || []);
    if (on) cur.add(key); else cur.delete(key);
    onChange({ ...p, modes: Array.from(cur) });
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">Enable screen recording</div>
          <div className="text-xs text-muted-foreground">Agents will be asked to share their screen once per shift.</div>
        </div>
        <Switch
          data-testid={tid("sr-enabled")}
          checked={!!p.enabled}
          disabled={disabled}
          onCheckedChange={(v) => onChange({ ...p, enabled: v })}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase text-muted-foreground">Recording modes</Label>
        {MODES.map((m) => (
          <label key={m.key} className="flex items-center gap-3 rounded-lg border p-2.5">
            <Switch
              data-testid={tid(`sr-mode-${m.key}`)}
              checked={(p.modes || []).includes(m.key)}
              disabled={disabled || !p.enabled}
              onCheckedChange={(v) => setModes(m.key, v)}
            />
            <div>
              <div className="text-sm">{m.label}</div>
              <div className="text-xs text-muted-foreground">{m.hint}</div>
            </div>
          </label>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center justify-between rounded-lg border p-2.5">
          <div className="text-sm">Include audio (system + mic)</div>
          <Switch
            data-testid={tid("sr-audio")}
            checked={p.audio !== false}
            disabled={disabled || !p.enabled}
            onCheckedChange={(v) => onChange({ ...p, audio: v })}
          />
        </div>
        <div className="flex items-center justify-between rounded-lg border p-2.5">
          <div>
            <div className="text-sm">Require full screen</div>
            <div className="text-[11px] text-muted-foreground">Reject single window / tab shares</div>
          </div>
          <Switch
            data-testid={tid("sr-require-fullscreen")}
            checked={p.require_full_screen !== false}
            disabled={disabled || !p.enabled}
            onCheckedChange={(v) => onChange({ ...p, require_full_screen: v })}
          />
        </div>
        <div className="rounded-lg border p-2.5">
          <Label className="text-xs text-muted-foreground">Quality</Label>
          <Select value={p.quality || "medium"} disabled={disabled || !p.enabled}
                  onValueChange={(v) => onChange({ ...p, quality: v })}>
            <SelectTrigger data-testid={tid("sr-quality")} className="mt-1 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              {QUALITIES.map((q) => <SelectItem key={q} value={q}>{q[0].toUpperCase() + q.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {showRetention && (
        <div className="rounded-lg border p-2.5">
          <Label className="text-xs text-muted-foreground">Retention (auto-purge older than)</Label>
          <Select value={String(p.retention_days || 90)} disabled={disabled}
                  onValueChange={(v) => onChange({ ...p, retention_days: Number(v) })}>
            <SelectTrigger data-testid={tid("sr-retention")} className="mt-1 h-8 w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RETENTIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

function fmtDuration(s) {
  s = Number(s || 0);
  const m = Math.floor(s / 60), sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}
function fmtLocal(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso).replace("T", " ").slice(0, 19);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
function fmtSize(b) {
  b = Number(b || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

export default function ScreenRecordingPage() {
  const { canWrite } = useCurrentModulePerms();
  const [tab, setTab] = useState("policy");

  // Policy state
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [campOptions, setCampOptions] = useState([]);
  const [newCamp, setNewCamp] = useState("");

  // Recordings state
  const [recs, setRecs] = useState([]);
  const [flags, setFlags] = useState([]);
  const [loadingFlags, setLoadingFlags] = useState(false);
  const [filters, setFilters] = useState({ agents: [], campaigns: [] });
  const [fAgent, setFAgent] = useState("all");
  const [fCamp, setFCamp] = useState("all");
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [player, setPlayer] = useState(null); // { rec, url }

  const loadCfg = () => {
    api.get("/admin/screen-recording/config").then((r) => {
      setCfg({ default: r.data.default, campaigns: r.data.campaigns || {} });
      setCampOptions(r.data.campaign_options || []);
    }).catch((e) => toast.error(formatErr(e)));
  };
  useEffect(() => { loadCfg(); }, []);
  useEffect(() => {
    api.get("/admin/screen-recording/recordings/filters")
      .then((r) => setFilters(r.data || { agents: [], campaigns: [] }))
      .catch(() => {});
  }, []);

  const loadRecs = () => {
    setLoadingRecs(true);
    const params = {};
    if (fAgent !== "all") params.agent_id = fAgent;
    if (fCamp !== "all") params.campaign_id = fCamp;
    api.get("/admin/screen-recording/recordings", { params })
      .then((r) => setRecs(r.data.recordings || []))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoadingRecs(false));
  };
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (tab === "recordings") loadRecs();
  }, [tab, fAgent, fCamp]);

  useEffect(() => {
    if (tab !== "compliance") return;
    setLoadingFlags(true);
    api.get("/admin/screen-recording/flags")
      .then((r) => setFlags(r.data.flags || []))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoadingFlags(false));
  }, [tab]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/screen-recording/config", cfg);
      toast.success("Screen recording policy saved");
      loadCfg();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const addCampaignOverride = () => {
    if (!newCamp) return;
    setCfg((c) => ({
      ...c,
      campaigns: { ...c.campaigns, [newCamp]: { ...c.default } },
    }));
    setNewCamp("");
  };
  const removeCampaignOverride = (cid) => {
    setCfg((c) => {
      const next = { ...c.campaigns };
      delete next[cid];
      return { ...c, campaigns: next };
    });
  };
  const campName = (id) => campOptions.find((c) => c.id === id)?.name || id;
  const availableCamps = useMemo(
    () => campOptions.filter((c) => !(cfg?.campaigns || {})[c.id]),
    [campOptions, cfg]);

  const openPlayer = async (rec) => {
    try {
      const r = await api.get(`/admin/screen-recording/recordings/${rec.id}/stream`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      setPlayer({ rec, url });
    } catch (e) { toast.error("Could not load video"); }
  };
  const closePlayer = () => {
    if (player?.url) URL.revokeObjectURL(player.url);
    setPlayer(null);
  };
  const download = async (rec) => {
    try {
      const r = await api.get(`/admin/screen-recording/recordings/${rec.id}/stream`, {
        params: { download: true }, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = rec.filename || `${rec.id}.webm`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("Download failed"); }
  };
  const del = async (rec) => {
    if (!window.confirm("Delete this screen recording permanently?")) return;
    try {
      await api.delete(`/admin/screen-recording/recordings/${rec.id}`);
      toast.success("Recording deleted");
      setRecs((rs) => rs.filter((r) => r.id !== rec.id));
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (!cfg) {
    return <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="screen-recording-page">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-primary/10 p-2"><MonitorPlay className="w-5 h-5 text-primary" /></div>
        <div>
          <h1 className="text-xl font-semibold">Screen Recording</h1>
          <p className="text-sm text-muted-foreground">Capture agent screens for QA. Stored on your own server; no cloud.</p>
        </div>
      </div>

      <div className="flex gap-2 border-b">
        <button data-testid="sr-tab-policy" onClick={() => setTab("policy")}
          className={`px-3 py-2 text-sm border-b-2 ${tab === "policy" ? "border-primary font-medium" : "border-transparent text-muted-foreground"}`}>
          Policy
        </button>
        <button data-testid="sr-tab-recordings" onClick={() => setTab("recordings")}
          className={`px-3 py-2 text-sm border-b-2 ${tab === "recordings" ? "border-primary font-medium" : "border-transparent text-muted-foreground"}`}>
          Recordings
        </button>
        <button data-testid="sr-tab-compliance" onClick={() => setTab("compliance")}
          className={`px-3 py-2 text-sm border-b-2 ${tab === "compliance" ? "border-primary font-medium" : "border-transparent text-muted-foreground"}`}>
          Compliance
        </button>
      </div>

      {tab === "policy" && (
        <div className="space-y-6">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold">Tenant default</h2>
            </div>
            <PolicyEditor
              value={cfg.default}
              showRetention
              disabled={!canWrite}
              onChange={(v) => setCfg((c) => ({ ...c, default: v }))}
            />
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold">Per-campaign overrides</h2>
              <div className="flex items-center gap-2">
                <Select value={newCamp} onValueChange={setNewCamp} disabled={!canWrite || !availableCamps.length}>
                  <SelectTrigger data-testid="sr-add-campaign-select" className="h-8 w-56"><SelectValue placeholder="Add campaign…" /></SelectTrigger>
                  <SelectContent>
                    {availableCamps.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" data-testid="sr-add-campaign-btn"
                  disabled={!canWrite || !newCamp} onClick={addCampaignOverride}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {Object.keys(cfg.campaigns || {}).length === 0 ? (
              <div className="text-sm text-muted-foreground py-4">No overrides — all campaigns use the tenant default.</div>
            ) : (
              <div className="space-y-4">
                {Object.entries(cfg.campaigns).map(([cid, pol]) => (
                  <div key={cid} className="rounded-xl border p-4" data-testid={`sr-campaign-${cid}`}>
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="secondary" className="text-xs">{campName(cid)}</Badge>
                      <button data-testid={`sr-remove-campaign-${cid}`} className="text-muted-foreground hover:text-destructive"
                        onClick={() => removeCampaignOverride(cid)}><X className="w-4 h-4" /></button>
                    </div>
                    <PolicyEditor
                      value={pol}
                      disabled={!canWrite}
                      idSuffix={`-${cid}`}
                      onChange={(v) => setCfg((c) => ({ ...c, campaigns: { ...c.campaigns, [cid]: v } }))}
                    />
                  </div>
                ))}
              </div>
            )}
          </Card>

          {canWrite && (
            <div className="flex justify-end">
              <Button data-testid="sr-save-btn" onClick={save} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save policy
              </Button>
            </div>
          )}
        </div>
      )}

      {tab === "recordings" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Select value={fAgent} onValueChange={setFAgent}>
              <SelectTrigger data-testid="sr-filter-agent" className="h-8 w-48"><SelectValue placeholder="All agents" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All agents</SelectItem>
                {filters.agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fCamp} onValueChange={setFCamp}>
              <SelectTrigger data-testid="sr-filter-campaign" className="h-8 w-48"><SelectValue placeholder="All campaigns" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All campaigns</SelectItem>
                {filters.campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Card className="p-0 overflow-hidden">
            {loadingRecs ? (
              <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : recs.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground">
                <Film className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No screen recordings yet.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="sr-recordings-table">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">Agent</th>
                    <th className="text-left px-4 py-2">Campaign</th>
                    <th className="text-left px-4 py-2">Mode</th>
                    <th className="text-left px-4 py-2">Started</th>
                    <th className="text-left px-4 py-2">Duration</th>
                    <th className="text-left px-4 py-2">Size</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recs.map((r) => (
                    <tr key={r.id} className="border-t hover:bg-muted/30" data-testid={`sr-row-${r.id}`}>
                      <td className="px-4 py-2">{r.agent_name || r.agent_id}</td>
                      <td className="px-4 py-2">{r.campaign_name || "—"}</td>
                      <td className="px-4 py-2"><Badge variant="outline" className="text-xs capitalize">{r.mode}</Badge></td>
                      <td className="px-4 py-2 text-muted-foreground">{fmtLocal(r.started_at)}</td>
                      <td className="px-4 py-2">{fmtDuration(r.duration_seconds)}</td>
                      <td className="px-4 py-2">{fmtSize(r.size)}</td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" data-testid={`sr-play-${r.id}`}
                            disabled={!r.has_video} onClick={() => openPlayer(r)}>
                            <MonitorPlay className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" data-testid={`sr-download-${r.id}`}
                            disabled={!r.has_video} onClick={() => download(r)}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" data-testid={`sr-delete-${r.id}`}
                            className="text-destructive" onClick={() => del(r)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}

      {tab === "compliance" && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Agents who declined the screen-share prompt or tried to share a single window/tab instead of their full screen.
          </p>
          <Card className="p-0 overflow-hidden">
            {loadingFlags ? (
              <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : flags.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground">
                <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No compliance flags — every agent shared their screen correctly.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="sr-flags-table">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">Agent</th>
                    <th className="text-left px-4 py-2">Issue</th>
                    <th className="text-left px-4 py-2">Shared</th>
                    <th className="text-left px-4 py-2">When</th>
                  </tr>
                </thead>
                <tbody>
                  {flags.map((f) => (
                    <tr key={f.id} className="border-t hover:bg-muted/30" data-testid={`sr-flag-${f.id}`}>
                      <td className="px-4 py-2">{f.agent_name || f.agent_id}</td>
                      <td className="px-4 py-2">
                        {f.reason === "wrong_surface"
                          ? <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">Shared a window/tab</Badge>
                          : <Badge variant="outline" className="text-xs border-rose-400 text-rose-600">Declined prompt</Badge>}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground capitalize">{f.surface || "—"}</td>
                      <td className="px-4 py-2 text-muted-foreground">{fmtLocal(f.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}
      <Dialog open={!!player} onOpenChange={(o) => { if (!o) closePlayer(); }}>
        <DialogContent className="max-w-3xl" data-testid="sr-player-dialog">
          <DialogHeader>
            <DialogTitle>{player?.rec?.agent_name} — {player?.rec?.campaign_name || "screen"}</DialogTitle>
          </DialogHeader>
          {player?.url && (
            <video data-testid="sr-player-video" src={player.url} controls autoPlay
              className="w-full rounded-lg bg-black max-h-[70vh]" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
